<script setup lang="ts">
import { PERMISSIONS } from '#imports'
import { useContentValidation } from '~/stores/contentValidation'
import { useEditor } from '~/stores/editor'

const contentValidation = useContentValidation()
const editor = useEditor()
const route = useRoute()
const policyId = computed(() => (route.params as { id: string }).id)

const quickDrawerOpen = ref(false)
const integrityPopupOpen = ref(false)

const errorsByPolicy = computed(() => {
  if (!policyId.value) {
    return []
  }

  return contentValidation.errorsByPolicy(policyId.value)
})

const warningsByPolicy = computed(() => {
  if (!policyId.value) {
    return []
  }

  return contentValidation.warningsByPolicy(policyId.value)
})

const integrityIssues = computed(() => {
  const errors = contentValidation.errorsByPolicy(policyId.value) || []
  const warnings = contentValidation.warningsByPolicy(policyId.value) || []
  return { errors, warnings }
})

onMounted(() => {
  editor.getPolicies()
  document.body.style.overscrollBehavior = 'none'
})

onBeforeUnmount(() => {
  document.body.style.overscrollBehavior = ''
})
</script>

<template>
  <AuthPermissionsGuard
    :permissions="[PERMISSIONS.writeTeamContent, PERMISSIONS.readTeamContent]"
  >
    <div class="flex w-full">
      <EditorQuickDrawer
        v-show="quickDrawerOpen"
        @close="quickDrawerOpen = false"
      />
      <div class="bg-surface-bg min-h-screen w-full">
        <header
          class="flex h-10 w-full items-center justify-between border-b px-4"
        >
          <div class="flex items-center space-x-2">
            <UiButton
              class="h-6"
              :variant="quickDrawerOpen ? undefined : 'outlined'"
              size="xs"
              @click="quickDrawerOpen = !quickDrawerOpen"
            >
              <span class="flex items-center space-x-2">
                <i class="i-ph-list-magnifying-glass inline-block size-4" />
                <span>Policies</span>
              </span>
            </UiButton>

            <AuthPermissionsGuard :permissions="[PERMISSIONS.writeTeamContent]">
              <NuxtLink
                to="/policy/create"
                class="btn bg-secondary hover:bg-secondary-800 h-6 text-xs text-white"
              >
                Create new
              </NuxtLink>
            </AuthPermissionsGuard>

            <div
              v-if="errorsByPolicy.length"
              class="bg-error-300 border-error text-error-700 hover:bg-error-400 flex cursor-pointer items-center space-x-2 rounded-full border-2 px-2 py-1 transition-colors"
              @click="integrityPopupOpen = true"
            >
              <div class="i-ph-warning block h-5 w-5" />

              <p class="body-2">{{ errorsByPolicy[0]!.message }}</p>

              <p v-if="errorsByPolicy.length > 1">
                Total issues: {{ errorsByPolicy.length }}
              </p>
            </div>
            <div
              v-else-if="warningsByPolicy.length"
              class="bg-warning-300 border-warning text-warning-700 hover:bg-warning-400 flex cursor-pointer items-center space-x-2 rounded-full border-2 px-2 py-1 transition-colors"
              @click="integrityPopupOpen = true"
            >
              <div class="i-ph-warning block h-5 w-5" />

              <p class="body-2">{{ warningsByPolicy[0]!.message }}</p>

              <p v-if="warningsByPolicy.length > 1">
                Total issues: {{ warningsByPolicy.length }}
              </p>
            </div>
          </div>

          <NuxtLink
            to="/"
            class="btn h-6 border border-neutral-300 text-neutral-800 hover:border-neutral-600 hover:bg-neutral-100"
          >
            Exit
            <span class="i-ph-sign-out ml-2" />
          </NuxtLink>
        </header>

        <slot />
      </div>
    </div>

    <!-- Integrity Issues Popup -->
    <ClientOnly>
      <UiDialog v-model="integrityPopupOpen" title="Policy Integrity Issues">
        <div class="flex items-center justify-between border-b px-6 py-4">
          <h2 class="text-lg font-semibold text-neutral-900">
            Policy Integrity Issues
          </h2>
          <button
            class="rounded-full p-1 transition-colors hover:bg-neutral-100"
            aria-label="Close dialog"
            @click="integrityPopupOpen = false"
          >
            <div class="i-ph-x-bold h-5 w-5 text-neutral-600" />
          </button>
        </div>
        <div class="p-6">
          <div
            v-if="
              integrityIssues.errors.length || integrityIssues.warnings.length
            "
            class="space-y-4"
          >
            <div class="space-y-2">
              <div class="space-y-2">
                <div
                  v-for="(issue, index) in integrityIssues.errors"
                  :key="`error-${index}`"
                  class="border-error-200 bg-error-300 flex items-start space-x-2 rounded border p-3"
                >
                  <div class="i-ph-warning mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div class="flex-1">
                    <div class="body-3 text-error-700">
                      {{ issue.message }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="integrityIssues.warnings.length" class="space-y-2">
              <div class="space-y-2">
                <div
                  v-for="(issue, index) in integrityIssues.warnings"
                  :key="`warning-${index}`"
                  class="border-warning-200 bg-warning-50 flex items-start space-x-2 rounded border p-3"
                >
                  <div
                    class="i-ph-warning text-warning mt-0.5 h-4 w-4 flex-shrink-0"
                  />
                  <div class="flex-1">
                    <div class="body-3 text-neutral-600">
                      {{ issue.message }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="body-2 text-neutral-600">
            No integrity issues found.
          </div>
        </div>
      </UiDialog>
    </ClientOnly>
    <template #fallback>
      <Teleport to="body">
        <div class="z-1000 fixed inset-0 flex items-center justify-center">
          <div>
            <h2 class="subtitle mb-4">
              You don't have permission to view this page
            </h2>
            <NuxtLink to="/" class="btn btn-outlined h-10">
              Back to dashboard
            </NuxtLink>
          </div>
        </div>
      </Teleport>
    </template>
  </AuthPermissionsGuard>
</template>
